import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const apiUrl = process.env.REACT_APP_API_URL;

// Thunk: Fetch products based on type
export const fetchProducts = createAsyncThunk(
  "products/fetchProducts",
  async (productType, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiUrl}/Products/`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        params: { type: productType },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch products"
      );
    }
  }
);

// Thunk: Fetch a product by its ID
export const fetchProductById = createAsyncThunk(
  "products/fetchProductById",
  async (productId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiUrl}/Products/${productId}/`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch product");
    }
  }
);

// Thunk: Fetch categories based on type
export const fetchCategories = createAsyncThunk(
  "products/fetchCategories",
  async (categoryType, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiUrl}/GetCategories/`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        params: { category_type: categoryType },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch categories"
      );
    }
  }
);

// Thunk: Create a new product
export const createProduct = createAsyncThunk(
  "products/createProduct",
  async (productData, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      Object.entries(productData).forEach(([key, value]) => {
        formData.append(key, value);
      });
      const response = await axios.post(`${apiUrl}/Products/Create`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to create product"
      );
    }
  }
);

// Thunk: Reorder products
export const reorderProducts = createAsyncThunk(
  "products/reorderProducts",
  async (orderedIds, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${apiUrl}/products/reorder/`,
        { ordered_ids: orderedIds },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to reorder products"
      );
    }
  }
);

const initialState = {
  products: {
    quantity: [],
    package: [],
    stock: [],
    all: [],
  },
  categories: {
    quantity: [],
    package: [],
    stock: [],
  },
  selectedProduct: null,
  loading: false,
  categoryLoading: false,
  creatingProduct: false,
  reordering: false,
  error: null,
  categoryError: null,
  createError: null,
  reorderError: null,
};

const productSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    // Local reorder without API call (optimistic update)
    localReorderProducts: (state, action) => {
      const { activeId, overId, productType } = action.payload;
      const productsKey = productType === 0 ? "all" : 
                         productType === 1 ? "quantity" : 
                         productType === 2 ? "package" : "stock";
      
      const items = state.products[productsKey];
      const oldIndex = items.findIndex(item => item.id === activeId);
      const newIndex = items.findIndex(item => item.id === overId);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newItems = [...items];
        const [removed] = newItems.splice(oldIndex, 1);
        newItems.splice(newIndex, 0, removed);
        state.products[productsKey] = newItems;
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch products
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        const {
          quantity = [],
          package: pkg = [],
          stock = [],
        } = action.payload || {};
        state.products.quantity = Array.isArray(quantity) ? quantity : [];
        state.products.package = Array.isArray(pkg) ? pkg : [];
        state.products.stock = Array.isArray(stock) ? stock : [];
        state.products.all = [
          ...state.products.quantity,
          ...state.products.package,
          ...state.products.stock,
        ];
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      });

    // Fetch product by ID
    builder
      .addCase(fetchProductById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedProduct = action.payload;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      });

    // Fetch categories
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.categoryLoading = true;
        state.categoryError = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categoryLoading = false;
        const {
          quantity = [],
          package: pkg = [],
          stock = [],
        } = action.payload || {};
        state.categories.quantity = Array.isArray(quantity) ? quantity : [];
        state.categories.package = Array.isArray(pkg) ? pkg : [];
        state.categories.stock = Array.isArray(stock) ? stock : [];
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.categoryLoading = false;
        state.categoryError = action.payload || action.error.message;
      });

    // Create product
    builder
      .addCase(createProduct.pending, (state) => {
        state.creatingProduct = true;
        state.createError = null;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.creatingProduct = false;
        state.products.all.push(action.payload);
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.creatingProduct = false;
        state.createError = action.payload || action.error.message;
      });

    // Reorder products
    builder
      .addCase(reorderProducts.pending, (state) => {
        state.reordering = true;
        state.reorderError = null;
      })
      .addCase(reorderProducts.fulfilled, (state, action) => {
        state.reordering = false;
      })
      .addCase(reorderProducts.rejected, (state, action) => {
        state.reordering = false;
        state.reorderError = action.payload || action.error.message;
      });
  },
});

export const { localReorderProducts } = productSlice.actions;
export default productSlice.reducer;